import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Divider, Flex, Modal, Space, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import Filters from "../../Components/Filters"
import PricingForm from "../../Components/Forms/PricingForm"
import Table from "../../Components/Table"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { serializeData } from "../../Utils/utils"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

const pageSize = 50

const { Title, Text } = Typography

const PricingPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  saveFetching,
  saveResult,
  statistics,
  ...props
}) => {
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [modalType, setModalType] = useState(null)
  const [selectPrice, setSelectPrice] = useState(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result

  const pricingList = results?.map((price) => price?.pricing_schema?.toLowerCase())

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const handleCloseModal = () => {
    setSelectPrice(null)
    setModalType(null)
  }

  const getPricingInfo = () => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      ...(columns.length && { columns: ["sequence_number", "id", ...columns].join(",") })
    }
    props.getPricing({ ...data })
  }

  useEffect(() => {
    document.title = "Pricing - Chargeback"
    props.getPricingFilterOptions()
  }, [])

  useEffect(() => {
    getPricingInfo()
  }, [businessAccountId, ordering, pageNumber, filters, myColumnsLength])

  useEffect(() => {
    if (saveResult) {
      let message
      if (saveResult === "create") {
        message = "New pricing scheme has been successfully created."
        getPricingInfo()
      } else if (saveResult === "update") {
        message = "Pricing scheme has been successfully updated."
      }
      if (message) notification.success({ message, ...notificationConfig })
      props.clearSaveResult()
      handleCloseModal()
    }
  }, [saveResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        x: count ? "max-content" : 230 * myColumns.filter(({ active }) => active).length,
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      }
    }),
    [myColumnsLength, count, pageNumber]
  )

  const getTableColumns = useMemo(() => {
    let allColumns = getColumns()
    return [
      allColumns["id"],
      //allColumns['status'],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data, t })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  function handleSelectRow(priceData, type) {
    if (type === "settings") {
      setSelectPrice(priceData)
      setModalType("updatePricing")
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,
        //transitionName: '',

        title: "Delete pricing scheme",
        content:
          "Are you sure you want to delete this pricing scheme? This action can NOT be undone and all business accounts using it will be switched to Default pricing scheme automatically.",

        cancelText: t("Cancel"),
        okText: t("Delete"),
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deletePricing(priceData.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    }
  }

  const confirmMinMaxPrice = (onOk) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: "Are you sure you want to save these prices?",
      content:
        "Some prices are outside the standard range. Please confirm if everything is correct before saving.",

      cancelText: t("Cancel"),
      okText: t("Confirm"),
      onOk() {
        onOk()
      }
    })
  }

  const confirmUpdatePrice = (onOk) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: "Saving prices",
      content:
        "Are you sure you want to save the changes? This action can NOT be undone. The new prices will be immediately applied to all business accounts using the current pricing scheme.",

      cancelText: t("Cancel"),
      okText: t("Save"),
      onOk() {
        onOk()
      }
    })
  }

  const createOrUpdatePrice = (data) => {
    if (selectPrice) {
      confirmUpdatePrice(() => props.updatePricing({ id: selectPrice.id, ...data }))
    } else {
      props.createPricing(data)
    }
  }

  const handleSubmitForm = (data) => {
    let finalData

    if (selectPrice && data?.pricing_items) {
      const initialPricingItems = [
        ...selectPrice?.ethoca_price_levels,
        ...selectPrice?.rdr_1_price_levels,
        ...selectPrice?.cdrn_price_levels
      ]

      const finalPricingItemsWithId = data?.pricing_items
        ?.filter((price) => price?.id)
        .map((price) => price?.id)

      const deletedPricingItems = [...initialPricingItems]
        ?.filter((el) => !finalPricingItemsWithId?.includes(el.id))
        ?.map((el) => ({ ...el, is_delete: true }))

      finalData = {
        ...data,
        pricing_items: [...data?.pricing_items, ...deletedPricingItems]
      }
    } else {
      finalData = { ...data }
    }

    const checkPrices = () => {
      return Object.keys(data).every((key) => {
        if (key.includes("_price")) {
          const value = data[key]
          return value >= 10 && value <= 40
        }

        if (key === "pricing_items") {
          return data[key].every(({ price }) => price >= 10 && price <= 40)
        }

        return true // skip keys that don't contain _price
      })
    }

    if (checkPrices()) {
      createOrUpdatePrice(finalData)
    } else {
      confirmMinMaxPrice(() => createOrUpdatePrice(finalData))
    }
  }

  return (
    <div className="pricing-invoices-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        Pricing
      </Title>

      <Divider />

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          withoutAdd={true}
          showClearBtn={!isFiltersDefault}
          onClearFilters={props.clearPricingFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <Button
            className="flex center"
            icon={<Icon type="plus" style={{ fontSize: 20 }} />}
            onClick={() => {
              setModalType("newPricing")
            }}
          >
            New pricing scheme
          </Button>
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        locale={{
          emptyText: !statistics?.["pricing_counter"] ? (
            <EmptyComponent type="no_data_available" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type={"menu_search"} />}
                onClick={props.clearPricingFilters}
              >
                {t("Clear search")}
              </Button>
            </EmptyComponent>
          )
        }}
      />

      <Modal
        open={!!modalType}
        centered={!isMobile}
        width={442}
        title={"Pricing scheme"}
        footer={null}
        closable="true"
        onCancel={handleCloseModal}
        destroyOnClose
        classNames={{ content: "clear-bg" }}
      >
        <Spin size="large" spinning={saveFetching}>
          <PricingForm
            optionsType={filterOptions?.["type"] || []}
            pricingList={pricingList}
            initialValues={selectPrice}
            onSubmit={handleSubmitForm}
            onClose={handleCloseModal}
          />
        </Spin>
      </Modal>
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.pricing.visibleFiltersAdmin,

  defaultColumnsAdmin: state.pricing.defaultColumnsAdmin,

  columnsAdmin: state.pricing.columnsAdmin,

  fetching: state.pricing.fetching,
  result: state.pricing.result,

  saveFetching: state.pricing.saveFetching,
  saveResult: state.pricing.saveResult,

  filters: state.pricing.filters,
  filterOptions: state.pricing.filterOptions,

  statistics: state.businessAccounts.statistics
})

const mapDispatchToProps = (dispatch) => ({
  getPricingFilterOptions: () => dispatch(Creators.getPricingFilterOptionsRequest()),

  getPricing: (params) => dispatch(Creators.getPricingRequest(params)),
  createPricing: (data) => dispatch(Creators.createPricingRequest(data)),
  updatePricing: (data) => dispatch(Creators.updatePricingRequest(data)),
  deletePricing: (id) => dispatch(Creators.deletePricingRequest(id)),

  changeFilters: (filters) => dispatch(Creators.pricingSetProp("filters", filters)),
  clearSaveResult: () => dispatch(Creators.pricingSetProp("saveResult", null)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.pricingSetProp(visibleFiltersType, filters)),
  clearPricingFilters: () => dispatch(Creators.clearPricingFilters())
})

export default connect(mapStateToProps, mapDispatchToProps)(PricingPage)
